import React, { useEffect, useMemo, useState } from "react";
import {
	AntdButton,
	AntdSelect,
	FilterContainer,
	Inner,
	QuotationSection,
	Section,
} from "./styles.js";
import {
	COLORS,
	Typo,
} from "../../../../../../components/layout/theme/index.js";
import { useFilter } from "../../../../../../contexts/FilterContext.js";
import { IoRainyOutline } from "react-icons/io5";
import { FaFire, FaLifeRing } from "react-icons/fa";
import { BiAlarmExclamation, BiLock } from "react-icons/bi";
import { Select } from "antd";
import {
	get_quotation_by_id,
	get_quotations,
} from "../../../../../../api/endpoints/quotations.js";
import QuotationCardList from "../../../../../../components/common/quotation-card-list/QuotationCardList.js";
import { useReport } from "../../../../../../contexts/ReportContext.js";
import { useMatomo } from "@datapunt/matomo-tracker-react";
const { Option } = Select;

// Constante con la configuración de los filtros de cobertura, para evitar
// duplicar el JSX y facilitar el mantenimiento (agregar/quitar filtros)
const COVERAGE_FILTERS = [
	{ id: 1, label: "Granizo", Icon: IoRainyOutline, filterKey: "hasHail" },
	{ id: 2, label: "Auxilio", Icon: FaLifeRing, filterKey: "hasLife" },
	{ id: 3, label: "Robo", Icon: BiAlarmExclamation, filterKey: "hasSteal" },
	{ id: 4, label: "Incendio", Icon: FaFire, filterKey: "hasFire" },
	{
		id: 5,
		label: "Cristales y cerradura",
		Icon: BiLock,
		filterKey: "hasCrystal",
	},
];

// Helper para obtener el precio final de una cotización, usado para ordenar.
// quotation.price ya es el "precio_efectivo" que viene del backend con el
// descuento ya aplicado, por lo que no debe recalcularse acá.
const getFinalPrice = (quotation) => quotation.price;


const QuotationResults = ({ nextSubStep, nextMainStep, isMobile }) => {
	const {
		selectedOptions,
		setSelectedOptions,
		optionType,
		setOptionType,
		setSelectedQuotation,
	} = useFilter();
	const { updateReportForm } = useReport();
	const [quotations, setQuotations] = useState([]);

	const [sortOption, setSortOption] = useState("price"); // 'price', 'priceDesc' o 'coverage'
	const { trackEvent } = useMatomo();

	useEffect(() => {
		const fetch_quotations = async () => {
			try {
				const res = await get_quotations();
				setQuotations(res);
			} catch (error) {
				console.error("Error al cargar cotizaciones:", error);
				setQuotations([]);
			}
		};

		fetch_quotations();
	}, []);

	// Filtra las cotizaciones según los filtros de cobertura seleccionados
	const filterQuotations = (currentSelectedOptions, quotationsToFilter) => {
		return quotationsToFilter.filter((quotation) => {
			if (!quotation) {
				console.warn("Found null or undefined quotation:", quotation);
				return false;
			}

			// Aplicar filtros usando las propiedades has* que vienen de config
			return COVERAGE_FILTERS.every(
				({ id, filterKey }) =>
					!currentSelectedOptions.includes(id) ||
					quotation[filterKey] === true
			);
		});
	};

	// Ordena las cotizaciones según el criterio seleccionado
	const sortQuotations = (quotationsToSort, sortBy) => {
		const sorted = [...quotationsToSort];

		if (sortBy === "price") {
			return sorted.sort((a, b) => getFinalPrice(a) - getFinalPrice(b));
		}
		if (sortBy === "priceDesc") {
			return sorted.sort((a, b) => getFinalPrice(b) - getFinalPrice(a));
		}
		if (sortBy === "coverage") {
			return sorted.sort((a, b) => a.coverage.localeCompare(b.coverage, "es"));
		}

		return sorted;
	};

	// Las cotizaciones filtradas y ordenadas se derivan de `quotations`,
	// `selectedOptions` y `sortOption` mediante useMemo, evitando mantener
	// un estado adicional duplicado y sincronizado manualmente.
	const filteredQuotations = useMemo(() => {
		const filtered = filterQuotations(selectedOptions, quotations);
		return sortQuotations(filtered, sortOption);
	}, [quotations, selectedOptions, sortOption]);

	const handleIconClick = (option) => {
		setSelectedOptions((prevSelectedOptions) => {
			if (prevSelectedOptions.includes(option)) {
				return prevSelectedOptions.filter((item) => item !== option);
			}
			return [...prevSelectedOptions, option];
		});
	};

	const fetch_quotation_by_id = (id) => {
		const res = get_quotation_by_id(id);
		const updateValues = {
			quotationId: res[0].id,
		};

		setSelectedQuotation(res[0]);

		updateReportForm(updateValues);
	};

	const handleConfirmQuotation = (item) => {
		trackEvent({
			category: "button-click",
			action: "next-button",
			name: `step2.quotation-(2)quoter::${JSON.stringify(item)}`,
		});
		fetch_quotation_by_id(item.id);
		nextMainStep(2);
	};

	const handleOptionChange = (value) => {
		setOptionType(value);

		switch (value) {
			case 1:
				setSelectedOptions([]);
				break;
			case 2:
				setSelectedOptions([1, 2]);
				break;
			case 3:
				setSelectedOptions([1, 2, 3, 4, 5]);
				break;
			default:
				break;
		}
	};

	const handleSortChange = (value) => {
		setSortOption(value);
	};

	useEffect(() => {
		if (optionType === 1 && selectedOptions.length !== 0) {
			setOptionType(4);
		}
		if (optionType === 2 && selectedOptions.length !== 2) {
			setOptionType(4);
		}
		if (optionType === 3 && selectedOptions.length !== 5) {
			setOptionType(4);
		}
	}, [selectedOptions, optionType, setOptionType]);


	return (
		<Inner>
			<Typo level={isMobile ? 3 : 2} style={{ textAlign: "center" }}>
				Estos son los planes{" "}
				<AntdSelect
					value={optionType}
					onChange={handleOptionChange}
					style={{ maxWidth: 220 }}
				>
					<Option value={1}>
						<Typo level={isMobile ? 3 : 2} type="secondary">
							básicos
						</Typo>
					</Option>
					<Option value={2}>
						<Typo level={isMobile ? 3 : 2} type="secondary">
							intermedios
						</Typo>
					</Option>
					<Option value={3}>
						<Typo level={isMobile ? 3 : 2} type="secondary">
							completos
						</Typo>
					</Option>
					<Option value={4}>
						<Typo level={isMobile ? 3 : 2} type="secondary">
							personalizados
						</Typo>
					</Option>
				</AntdSelect>
				que encontramos para vos
			</Typo>
			<FilterContainer isMobile={isMobile}>
				<Typo level={306}>Filtrar: </Typo>
				<Section style={{ gap: 10 }} isMobile={isMobile}>
					{COVERAGE_FILTERS.map(({ id, label, Icon }) => {
						const isSelected = selectedOptions.includes(id);
						return (
							<AntdButton
								key={id}
								isMobile={isMobile}
								icon={
									<Icon
										style={{
											color: isSelected
												? COLORS.Secondary
												: COLORS.MediumGrey,
										}}
										size={15}
									/>
								}
								type={isSelected ? "primary" : "default"}
								ghost
								onClick={() => handleIconClick(id)}
								style={{
									marginRight: 10,
									borderColor: isSelected ? COLORS.Secondary : COLORS.Grey,
								}}
							>
								<Typo type={isSelected ? "secondary" : "mediumGrey"}>
									{label}
								</Typo>
							</AntdButton>
						);
					})}
				</Section>
				<Section style={{ gap: 10 }}>
					<Typo level={306}>Ordenar: </Typo>
					<Select
						defaultValue="price"
						value={sortOption}
						onChange={handleSortChange}
					>
						<Select.Option key="price" value="price">
							Menor precio
						</Select.Option>
						<Select.Option key="priceDesc" value="priceDesc">
							Mayor precio
						</Select.Option>
						<Select.Option key="coverage" value="coverage">
							Plan de cobertura
						</Select.Option>
					</Select>
				</Section>
			</FilterContainer>
			<QuotationSection isMobile={isMobile}>
				{filteredQuotations.map((item) => (
					<QuotationCardList
						key={item.id}
						item={item}
						handleConfirmQuotation={handleConfirmQuotation}
						isMobile={isMobile}
					/>
				))}
			</QuotationSection>
		</Inner>
	);
};

export default QuotationResults;
